import i18n from "@i18n";

import { getSubscriptionLifecycleState } from "@utils/subscriptionLifecycleState";
import { setGeoLocation } from "@utils/redux/slices/geoLocation";
import { setRouteId } from "@utils/redux/slices/routeId";
import getMeristemDefaultValue from "@utils/meristemContext/meristemContextDefaults";
import getContext, { FetchInAppServerContextError } from "./context";
import { getEmailAndName } from "./urlParams";
import { USER_IS_BOT } from "./botDetector";
import { setUserIdInSentryScope } from "./sentry";

import store, { observeStore } from "./redux/store";
import { updateRecommendedPlan } from "./redux/slices/recommendedPlan";
import { updatePlans } from "./redux/slices/plans";
import { updateServerContext } from "./redux/slices/serverContext";
import {
  updateUserData,
  initializeNewUser,
  fetchAndInitializeExistingUser,
} from "./redux/slices/userData";
import { updateTrialFromServerContext } from "./redux/slices/trial";
import { setLanguage } from "./redux/slices/language";
import { setDeviceInformation } from "./redux/slices/deviceInformation";

import ConversionTracker from "./services/ConversionTracker";
import EventTracker from "./services/EventTracker";
import OptimizelyExperiment from "./services/OptimizelySXExperiment";
import ExperimentTracker from "./services/ExperimentTracker";
import VisitTracker from "./services/VisitTracker";

export default async function initializeApplication() {
  try {
    // 1 - get the serverContext from cache or refetch.
    const serverContext = await getServerContextOnInitialization();
    // 2 - get the meristemContext data.
    const meristemContext = getMeristemContext();
    // 3 - update the UserData slice in redux (also, fetch userData if an existing user).
    await updateUserDataSlice(serverContext, meristemContext);
    // 4 - rehydrate redux state with serverContext.
    await updateStoreWithServerContext(serverContext);
    // 5 - update store with data from meristemContext
    initializeStoreWithMeristemContextData(meristemContext);
    // 6 - initialize the various services we use to sync data.
    initializeGlobalServices(serverContext, meristemContext, USER_IS_BOT);
    // 7 - Add mixpanel tracking of the user's state in the subscription lifecycle.
    trackSubscriptionLifecycle(serverContext, meristemContext);
  } catch (e) {
    if (e instanceof FetchInAppServerContextError) {
      window.location = `/identification-error.html${window.location.search}`;
    } else {
      window.location = `/error.html${window.location.search}`;
    }

    // Throw another error to stop the rest of the JS from running after the redirect
    console.error(e);
    throw new Error("Failed to initialize App");
  }

  // Initialize tracking utilizing the embedded window.meristemContext.
  initializeAndTrackMeristemV2Experiment();
}

/**
 * Initialize global services and variables for use within our components.
 */
function initializeGlobalServices(
  serverContext,
  meristemContext,
  // NOTE(patrick): We had issues with bots with duplicated cookies or application
  // state getting lumped into one experiment variation which would mess up
  // experiment reporting. We use a bot detection library and make sure they get
  // a minimal experience
  userIsBot = false
) {
  // Set up Sentry and set the user.
  setUserIdInSentryScope(serverContext.user_id);
  window.serverContext = serverContext;

  // Update i18n language to match serverContext language.
  if (i18n && meristemContext.language_code !== i18n.language) {
    i18n.changeLanguage(meristemContext.language_code);
  }

  window.experimentTracker = new ExperimentTracker();
  window.visitTracker = new VisitTracker(serverContext.session_cookie_domain);

  // Set up the event trackers and make them available globally.
  window.tracker = new EventTracker(userIsBot);

  // Set up conversion tracker for pixel tracking.
  window.conversionTracker = new ConversionTracker();

  window.osxe = new OptimizelyExperiment();

  window.reduxState = store.getState;

  document.querySelector("html").lang = meristemContext.language_code;
}

/**
 * Get serverContext on initialization from cache with retry if fetching from cache fails.
 */
async function getServerContextOnInitialization() {
  try {
    return await window.serverContextLoaded;
  } catch (err) {
    console.error(err);
    window.serverContextLoaded = getContext();
    return window.serverContextLoaded;
  }
}

/**
 * Returns the meristemContext passed by buyflow-worker, or it fallbacks to default for development environment
 */
export function getMeristemContext() {
  return window?.meristemContext?.context || getMeristemDefaultValue();
}

/**
 * Update UserData slice (and possibly fetch an existing user's UserData).
 * @param {*} serverContext
 * @param {*} meristemContext
 */
async function updateUserDataSlice(serverContext, meristemContext) {
  // Fetch user from backend if they are in the database.
  // Guard against bots, don't attempt to fetch userData if we detect a bot.
  if (serverContext.user_in_db !== false && !USER_IS_BOT) {
    await store.dispatch(
      fetchAndInitializeExistingUser(
        serverContext.user_id,
        serverContext,
        meristemContext
      )
    );
  } else {
    await store.dispatch(initializeNewUser(serverContext, meristemContext));
  }
  const emailAndName = getEmailAndName();
  if (Object.keys(emailAndName).length !== 0)
    await store.dispatch(updateUserData(emailAndName));
}

/**
 * After we fetch serverContext, Take the data and update the appropriate
 * redux slices with it.
 * @param {*} serverContext
 */
export async function updateStoreWithServerContext(serverContext) {
  await store.dispatch(updateServerContext(serverContext));
  await store.dispatch(updateTrialFromServerContext(serverContext));

  if (serverContext?.plans)
    await store.dispatch(updatePlans(serverContext.plans));
  if (serverContext?.plan)
    await store.dispatch(updateRecommendedPlan(serverContext.plan));
}

/**
 * Track changes to the user's state in the subscription lifecycle to mixpanel.
 *
 * 1. Adds tracking observer to the subscription slice in redux.
 * 2. Tracks initial state.
 * @param {*} serverContext
 * @param {*} meristemContext
 */
function trackSubscriptionLifecycle(serverContext, meristemContext) {
  // 1. Add an observer that tracks subscription lifecycle changes to Mixpanel
  // when subscription determinations change.
  const subscriptionDeterminationsSelector = (state) =>
    state.subscription.determinations;
  const trackSubscriptionLifecycleToMixpanel = (subscriptionDeterminations) => {
    const {
      subscriptionLifecycleState,
      sources,
    } = getSubscriptionLifecycleState(
      subscriptionDeterminations,
      meristemContext,
      window.location
    );

    if (subscriptionLifecycleState) {
      window.tracker.updateUserContext({
        visitorState: subscriptionLifecycleState,
      });
      window.tracker.trackGenericEvent("VisitorStateDetermined", {
        visitorState: subscriptionLifecycleState,
        ...sources,
        context_type: serverContext.context_type,
      });
    }
  };
  observeStore(
    subscriptionDeterminationsSelector,
    trackSubscriptionLifecycleToMixpanel
  );

  // 2. Track user's initial state in subscription lifecycle.
  trackSubscriptionLifecycleToMixpanel(
    store.getState().subscription.determinations
  );
}

/**
 * Initialize the Visit Tracker with Experiment tags and track OnMeristemExperimentDecided
 */
function initializeAndTrackMeristemV2Experiment() {
  const experimentsState = window?.meristemContext?.experimentsCookie || [];
  window.visitTracker.init(
    ExperimentTracker.formatMeristemExperimentStateVisitData(
      experimentsState
    ) || {}
  );

  experimentsState.forEach((experimentState) => {
    window.tracker.trackGenericEvent("OnMeristemExperimentDecided", {
      experimentName: experimentState.experimentName || null,
      variationName: experimentState.variationName || null,
    });
  });
}

/**
 * Initialize store with data from meristemContext
 * If the meristemContext window global variable is not available, the slices will fallback to default data
 */
function initializeStoreWithMeristemContextData(meristemContextData) {
  store.dispatch(setDeviceInformation(meristemContextData));
  store.dispatch(setGeoLocation(meristemContextData));
  store.dispatch(setLanguage(meristemContextData.language_code));
  store.dispatch(setRouteId(meristemContextData.route_id));
}
